import { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { DISPLAY_CURRENCIES, SMALL_MOBILE } from "common/constants";
import React, { useCallback, useContext, useMemo } from "react";
import { TradeActionsContext } from "@shared/features/trades/common/contexts";
import {
  LanguageNameSpaces,
  PrecisionType,
  TradeData,
  TradeDataWithKey,
} from "@shared/types";
import { ColumnType } from "@shared/common/CardsList/types";
import {
  compareTrades,
  TradesCardsSortKey,
} from "features/trades/common/tradesCardsSort.ts";
import useExpandedCardsSorters from "common/CardSorters/useExpandedCardsSorters";
import { TradeProgressTooltip } from "features/trades/common/TradeProgressTooltip";
import {
  NEW_LOOK_TRADE_CARD_COLUMN_SPANS,
  TRADE_CARD_COLUMN_SPANS,
} from "features/trades/common/constants";
import {
  TRADES_DATA_ALLOCATED_AMOUNT_BTC_HEADER,
  TRADES_DATA_ALLOCATED_AMOUNT_USD_HEADER,
  TRADES_DATA_BTC_AMOUNT_CHANGE_HEADER,
  TRADES_DATA_USD_AMOUNT_CHANGE_HEADER,
} from "@shared/features/trades/common/constants";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import TextWithPrecision from "common/Text/TextWithPrecision";
import { getTradeExtraDetailsRoute } from "utils/routes";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { TradesPageContext } from "@shared/features/trades/common/TradesPage.context";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import { checkTradeSearch } from "features/trades/common/tradeSearch";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants.ts";

export const useTradesHeaderHeight = () => {
  const { isBulkMode } = useContext(TradeActionsContext) || {};
  return (
    useMedia([`(max-width: ${SMALL_MOBILE})`], [56], 58) + (isBulkMode ? 48 : 0)
  );
};

interface UseTradesCardsAndSortersProps {
  data: TradeDataWithKey[];
  isOpenTrades: boolean;
  shouldShowTooltip?: Boolean;
}

export const useTradesCardsAndSorters = ({
  data,
  isOpenTrades,
  shouldShowTooltip,
}: UseTradesCardsAndSortersProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { isNewLook } = useTheme();
  const spans = isNewLook
    ? NEW_LOOK_TRADE_CARD_COLUMN_SPANS
    : TRADE_CARD_COLUMN_SPANS;
  const columns: ColumnType<TradeDataWithKey>[] = useMemo(
    () => [
      {
        title: t("table.headers.symbol"),
        span: spans[0],
        key: "symbol",
        sorter: (first, second) =>
          compareTrades(first, second, TradesCardsSortKey.SYMBOL, isOpenTrades),
      },
      {
        title: t("table.headers.amount"),
        span: spans[1],
        key: "amount",
        sorter: (first, second) =>
          compareTrades(first, second, TradesCardsSortKey.AMOUNT, isOpenTrades),
      },
      {
        title: (
          <div style={{ display: "flex" }}>
            {t("table.headers.progress")}
            {shouldShowTooltip && (
              <TradeProgressTooltip isOpenTrades={isOpenTrades} />
            )}
          </div>
        ),
        span: spans[2],
        key: "progress",
        sorter: (first, second) =>
          compareTrades(first, second, TradesCardsSortKey.PNL, isOpenTrades),
      },
    ],
    [isOpenTrades, shouldShowTooltip, i18n.language, spans]
  );

  const defaultSortFunction = useCallback(
    (a: TradeDataWithKey, b: TradeDataWithKey) => b.timestamp - a.timestamp,
    []
  );

  const { sortedData, cardsSorters, toggleExpanded, isExpanded } =
    useExpandedCardsSorters({
      data,
      columns,
      defaultSortFunction,
    });

  return {
    sortedData,
    cardsSorters,
    toggleExpanded,
    isExpanded,
  };
};

interface useTradeCardProps {
  allowTradeClick?: boolean;
}

export const useTradeCard = ({ allowTradeClick }: useTradeCardProps) => {
  const { historyPush } = useHistoryWithQuery();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { precisionConfig } = useContext(TradesPageContext)!;
  const { handleTradeActionClick } = useContext(TradeActionsContext)!;

  const allocatedHeader =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? TRADES_DATA_ALLOCATED_AMOUNT_BTC_HEADER
      : TRADES_DATA_ALLOCATED_AMOUNT_USD_HEADER;
  const amountChangeHeader: "usd_amount_change" | "btc_amount_change" =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? TRADES_DATA_BTC_AMOUNT_CHANGE_HEADER
      : TRADES_DATA_USD_AMOUNT_CHANGE_HEADER;

  const getTradeAmount = useCallback(
    (tradeData: TradeData) => (
      <TextWithPrecisionAndIcon
        precisionType={PrecisionType.BALANCE}
        precisionConfig={precisionConfig}
        text={tradeData[allocatedHeader]}
        round={true}
      />
    ),
    [precisionConfig, allocatedHeader]
  );

  const getTradeQuantity = useCallback(
    (tradeData: TradeData) => {
      return (
        <TextWithPrecision
          precisionConfig={precisionConfig}
          precisionType={PrecisionType.QUANTITY}
          text={tradeData.allocated_quantity}
          precisionCoin={tradeData.symbol}
          round={true}
        />
      );
    },
    [precisionConfig]
  );

  const openTradeExtraDetails = (tradeData: TradeData) => {
    if (allowTradeClick) {
      historyPush(
        getTradeExtraDetailsRoute(
          location.pathname,
          tradeData.id,
          TRADE_EXTRA_DETAILS_TABS.GENERAL
        )
      );
    }
  };

  return {
    allocatedHeader,
    amountChangeHeader,
    getTradeAmount,
    getTradeQuantity,
    openTradeExtraDetails,
    handleTradeActionClick,
  };
};

export const useTradesFlatList = () => {
  const { i18n } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { search } = useContext(PageWrapperContext)!;
  const {
    isOpenTrades,
    activeTrades,
    waitingCancellationTrades,
    isLoading,
    isFetching,
    getTradesQuery,
    inDrawer,
  } = useContext(TradesPageContext)!;

  const getFilteredData = (trades?: TradeData[]): TradeDataWithKey[] => {
    return (
      trades
        ?.filter((tradeData) => checkTradeSearch(tradeData, search.value))
        .map((tradeData) => ({
          ...tradeData,
          key: tradeData.id,
        })) || []
    );
  };

  const filteredActiveTrades = useMemo(
    () => getFilteredData(activeTrades),
    [activeTrades, search.value, i18n.language]
  );
  const filteredWaitingTrades = useMemo(
    () => getFilteredData(waitingCancellationTrades),
    [waitingCancellationTrades, search.value, i18n.language]
  );

  const onRefresh = useCallback(() => {
    try {
      getTradesQuery?.refetch();
    } catch {}
  }, []);

  const getNumOfTrades = (isWaitingTrades?: boolean) =>
    isOpenTrades
      ? isWaitingTrades
        ? waitingCancellationTrades?.length
        : activeTrades?.length
      : getTradesQuery?.data?.aggregated_data?.number_of_closed_trades;

  const { sortedData, cardsSorters, toggleExpanded, isExpanded } =
    useTradesCardsAndSorters({
      data: filteredActiveTrades,
      isOpenTrades,
    });

  const {
    sortedData: waitingTradesSortedData,
    cardsSorters: waitingTradesCardsSorters,
    toggleExpanded: waitingTradesToggleExpanded,
    isExpanded: waitingTradesIsExpanded,
  } = useTradesCardsAndSorters({
    data: filteredWaitingTrades,
    isOpenTrades,
  });

  const flatListData = useMemo(() => {
    const data: (
      | TradeDataWithKey
      | { isHeader: boolean; isWaitingTrades?: boolean }
    )[] = [];
    if (waitingCancellationTrades?.length) {
      data.push(
        { isHeader: true, isWaitingTrades: true },
        ...(waitingTradesSortedData || [])
      );
    }
    data.push(
      { isHeader: true, isWaitingTrades: false },
      ...(sortedData || [])
    );
    return data;
  }, [waitingCancellationTrades, waitingTradesSortedData, sortedData]);

  const isEmpty = (isWaiting?: boolean) => {
    const data = isWaiting ? waitingCancellationTrades : activeTrades;
    return !!data && !data?.length && !isLoading;
  };

  return {
    flatListData,
    cardsSorters,
    toggleExpanded,
    isExpanded,
    waitingTradesCardsSorters,
    waitingTradesToggleExpanded,
    waitingTradesIsExpanded,
    getNumOfTrades,
    isOpenTrades,
    isLoading,
    isFetching,
    searchValue: search.value,
    onRefresh,
    isEmpty,
    inDrawer,
    filteredActiveTrades,
  };
};
